import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, ChevronRight } from 'lucide-react';
import { ApplicationServiceSummary, DiscoveryStatus } from '../services/cmdb/ScopeService';

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<DiscoveryStatus, {
  label: string;
  icon: React.ReactNode;
  badgeCls: string;
  borderCls: string;
  barCls: string;
}> = {
  Discovered: {
    label: 'Discovered',
    icon: <CheckCircle2 size={14} />,
    badgeCls: 'bg-green-100 text-green-800 border-green-200',
    borderCls: 'border-green-400',
    barCls: 'bg-green-500',
  },
  Partial: {
    label: 'Partial',
    icon: <AlertTriangle size={14} />,
    badgeCls: 'bg-amber-100 text-amber-800 border-amber-200',
    borderCls: 'border-amber-400',
    barCls: 'bg-amber-500',
  },
  NotFound: {
    label: 'Not Found',
    icon: <XCircle size={14} />,
    badgeCls: 'bg-red-100 text-red-700 border-red-200',
    borderCls: 'border-red-400',
    barCls: 'bg-red-400',
  },
  Missing: {
    label: 'Missing',
    icon: <HelpCircle size={14} />,
    badgeCls: 'bg-gray-100 text-gray-500 border-gray-200',
    borderCls: 'border-gray-300',
    barCls: 'bg-gray-300',
  },
};

// ─── Single entity-type row inside a tile ─────────────────────────────────────

function EntityRow({ label, discovered, total, status }: {
  label: string;
  discovered: number;
  total: number;
  status: DiscoveryStatus;
}) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.Missing;
  return (
    <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
      <span className="text-gray-600 truncate max-w-[140px]" title={label}>{label}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-gray-400">{discovered}/{total}</span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${cfg.badgeCls}`}>
          {cfg.icon}
          {cfg.label}
        </span>
      </div>
    </div>
  );
}

// ─── Main tile ────────────────────────────────────────────────────────────────

interface ScopeEntityTileProps {
  service: ApplicationServiceSummary;
  onOpen: (sysId: string, appId: string) => void;
}

export default function ScopeEntityTile({ service, onOpen }: ScopeEntityTileProps) {
  const cfg = STATUS_CONFIG[service.overallStatus] ?? STATUS_CONFIG.Missing;
  const [expanded, setExpanded] = React.useState(false);

  const visibleRows = expanded
    ? service.entityCoverage
    : service.entityCoverage.slice(0, 4);

  return (
    <div
      className={`bg-white rounded-xl border-2 ${cfg.borderCls} shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col`}
    >
      {/* ── Tile header ── */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#1E3A5F] text-sm leading-snug truncate" title={service.name}>
              {service.name}
            </h3>
            {service.applicationId && (
              <p className="text-[10px] text-gray-400 mt-0.5 truncate" title={service.applicationId}>
                ID: {service.applicationId}
              </p>
            )}
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold flex-shrink-0 ${cfg.badgeCls}`}>
            {cfg.icon}
            {cfg.label}
          </span>
        </div>

        {/* Coverage progress bar */}
        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>Coverage</span>
            <span className="font-semibold text-gray-600">{service.coveragePercent}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${cfg.barCls}`}
              style={{ width: `${service.coveragePercent}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">
            {service.coveredEntityTypes} of {service.totalEntityTypes} entity types covered
          </p>
        </div>
      </div>

      {/* ── Entity type breakdown ── */}
      {service.entityCoverage.length > 0 && (
        <div className="px-4 pb-2">
          <div className="divide-y divide-gray-50">
            {visibleRows.map(ec => (
              <EntityRow
                key={ec.sysClassName}
                label={ec.label}
                discovered={ec.discovered}
                total={ec.total}
                status={ec.status}
              />
            ))}
          </div>
          {service.entityCoverage.length > 4 && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-[11px] text-[#0054A4] hover:underline mt-1"
            >
              {expanded
                ? 'Show less'
                : `+${service.entityCoverage.length - 4} more entity types`}
            </button>
          )}
        </div>
      )}

      {/* ── Empty state inside tile ── */}
      {service.entityCoverage.length === 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-400 italic">No relationships found in CMDB</p>
        </div>
      )}

      {/* ── CTA footer ── */}
      <div className="mt-auto border-t border-gray-100 px-4 py-2.5">
        <button
          onClick={() => onOpen(service.sysId, service.applicationId)}
          className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0054A4] hover:text-[#003A73] transition-colors"
        >
          Open in Service Mapper
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
